import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { useAuth } from './context/AuthContext';
import { NavigationBlockerProvider } from './context/NavigationBlockerContext';
import ProtectedRoute from './components/ProtectedRoute';
import RoleBasedRoute from './components/RoleBasedRoute';
import MainNavigation from './components/MainNavigation';
import Login from './pages/Login';
import CMSDashboard from './pages/CMSDashboard';
import ContentSourceEditor from './pages/ContentSourceEditor';
import ReviewEditor from './pages/ReviewEditor';
import UserManagement from './pages/UserManagement';
import Admin from './pages/Admin';
import PasswordChange from './pages/PasswordChange';
import AIExtraction from './pages/AIExtraction';
import './App.css';

// Component to handle role-based default redirect
const RoleBasedRedirect: React.FC = () => {
  const { authState } = useAuth();
  const userRole = authState.user?.role;

  // Role-based default redirects
  switch (userRole) {
    case 'reviewer':
      return <Navigate to="/cms/review" replace />;
    case 'editor':
      return <Navigate to="/cms/dashboard" replace />;
    case 'editor-reviewer':
      return <Navigate to="/cms/dashboard" replace />;
    case 'admin':
    case 'superadmin':
      return <Navigate to="/cms/dashboard" replace />;
    default:
      // If role is not recognized, default to CMS dashboard
      return <Navigate to="/cms/dashboard" replace />;
  }
};

function App() {
  return (
    <AuthProvider>
      <NavigationBlockerProvider>
        <Router>
        <Routes>
          <Route path="/login" element={<Login />} />

          <Route element={<ProtectedRoute />}>
            {/* Layout with navigation */}
            <Route path="/" element={<MainNavigation />}>

              {/* CMS Dashboard - All roles can access */}
              <Route path="/cms/dashboard" element={<CMSDashboard />} />

              {/* Content Source Editor - All roles can access (editors edit, reviewers review) */}
              <Route element={<RoleBasedRoute allowedRoles={['editor', 'editor-reviewer', 'reviewer', 'admin', 'superadmin']} />}>
                <Route path="/cms/editor/:id" element={<ContentSourceEditor />} />
                <Route path="/cms/editor" element={<ContentSourceEditor />} />
              </Route>

              {/* Review Editor - Reviewers, editor-reviewers, admins and superadmins */}
              <Route element={<RoleBasedRoute allowedRoles={['reviewer', 'editor-reviewer', 'admin', 'superadmin']} />}>
                <Route path="/cms/review/:id" element={<ContentSourceEditor />} />
                <Route path="/cms/review" element={<ReviewEditor />} />
              </Route>

              {/* AI Extraction - Only admins and superadmins */}
              <Route element={<RoleBasedRoute allowedRoles={['admin', 'superadmin']} />}>
                <Route path="/cms/ai-extraction" element={<AIExtraction />} />
              </Route>

              {/* Password Change - All users except admin and superadmin */}
              <Route element={<RoleBasedRoute allowedRoles={['reviewer', 'editor', 'editor-reviewer']} />}>
                <Route path="/password" element={<PasswordChange />} />
              </Route>

              {/* User Management - Only admins and superadmins */}
              <Route element={<RoleBasedRoute allowedRoles={['admin', 'superadmin']} />}>
                <Route path="/users" element={<UserManagement />} />
              </Route>

              {/* Admin Panel - Only admins and superadmins */}
              <Route element={<RoleBasedRoute allowedRoles={['admin', 'superadmin']} />}>
                <Route path="/admin" element={<Admin />} />
              </Route>

              {/* Role-based default redirect */}
              <Route index element={<RoleBasedRedirect />} />
            </Route>
          </Route>

          <Route path="*" element={<RoleBasedRedirect />} />
        </Routes>
      </Router>
      </NavigationBlockerProvider>
    </AuthProvider>
  );
}

export default App;
